// Rated Orders › Compensate — issue a wallet compensation / voucher to the booking's
// customer from the queue. Same write + push as Marketing › Vouchers (helpers/wallet.js),
// plus: bookingID + complaint flag on the wallet doc, a compensationIssued log entry and
// complaint.compensation on the booking so the queue shows what was already given.
import moment from 'moment';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ORDER_COLLECTION } from '../../../../config/env';
import { firestore } from '../../../../helpers/Firebase';
import { accessTypesObject, createNotification } from '../../../../helpers/Utils';
import { issueWalletVoucher } from '../../../../helpers/wallet';
import { SOFT_SELECT_STYLES } from './complaint';

const TYPES = [
  { value: 'Compensation', label: 'Compensation (apology credit)' },
  { value: 'Voucher', label: 'Voucher' },
];

const CompensateModal = ({ booking, user, isOpen, dismiss, onIssued }) => {
  const [form, setForm] = useState({ amount: '', description: 'Compensation', exp: moment().add(30, 'days').endOf('day').valueOf(), minAmount: '' });
  const [busy, setBusy] = useState(false);
  const amount = Number(form.amount);
  const valid = Number.isFinite(amount) && amount > 0 && form.exp && form.description && booking?.customer_id;
  const prev = booking?.complaint?.compensation;

  const submit = async () => {
    setBusy(true);
    const id = booking.key || booking.id || booking.bookingId;
    const now = Date.now();
    const who = { createdBy: accessTypesObject?.[user?.accessType]?.name || user?.accessType || 'Staff', createdByName: user?.displayName || user?.name || user?.email || '' };
    try {
      const voucher = {
        customer: `+${`${booking.country_code || ''}${booking.customer_number || ''}`.replace(/\D/g, '')}`,
        type: form.description === 'Voucher' ? 'voucher' : 'compensation',
        description: form.description,
        amount: String(amount),
        exp: Number(form.exp),
        addNew: true,
      };
      if (form.minAmount !== '' && Number(form.minAmount) > 0) voucher.minAmount = String(Number(form.minAmount));
      const [walletLogId] = await issueWalletVoucher({ userID: booking.customer_id, voucher, extra: { bookingID: id, complaint: true } });
      const compensation = { walletLogId, amount, description: form.description, exp: Number(form.exp), issuedAt: now, issuedBy: who.createdByName || who.createdBy };
      await firestore().collection(ORDER_COLLECTION).doc(id).update({
        'complaint.compensation': compensation,
        'complaint.compensationTotal': Number(booking?.complaint?.compensationTotal || 0) + amount,
        'complaint.lastActionAt': now,
        'complaint.lastActionBy': who.createdByName || who.createdBy,
        updated: now,
      });
      await firestore().collection('logs').doc(id).set({
        data: firestore.FieldValue.arrayUnion({ title: 'compensationIssued', created: now, ...who, amount, description: form.description, walletLogId, exp: Number(form.exp) }),
        updated: now,
      }, { merge: true }).catch((e) => console.error('compensation log failed', e));
      createNotification('success', `${form.description} of AED ${amount.toFixed(2)} issued to ${booking.customer_name || 'the customer'}`, 'Rated Orders');
      onIssued && onIssued(compensation);
      dismiss();
    } catch (e) {
      console.error('compensation failed', e);
      createNotification('error', e?.message || 'Could not issue the compensation', 'Rated Orders');
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={!!isOpen} toggle={dismiss} backdrop="static">
      <ModalHeader toggle={dismiss}>Compensate · order #{booking?.orderNumber}</ModalHeader>
      <ModalBody>
        <div className="mb-3 text-small text-muted">
          {booking?.customer_name} · +{`${booking?.country_code || ''}${booking?.customer_number || ''}`.replace(/\D/g, '')}
          {prev ? <div style={{ color: '#e08a00' }}>Already issued: {prev.description} AED {Number(prev.amount).toFixed(2)} on {moment(prev.issuedAt).format('DD/MM/YYYY')}{booking?.complaint?.compensationTotal > prev.amount ? ` (total AED ${Number(booking.complaint.compensationTotal).toFixed(2)})` : ''}</div> : null}
        </div>
        <Label>Type</Label>
        <Select options={TYPES} classNamePrefix="select" isSearchable={false} styles={SOFT_SELECT_STYLES} menuPlacement="auto" value={TYPES.find((t) => t.value === form.description)} onChange={(v) => setForm((f) => ({ ...f, description: v?.value || 'Compensation' }))} />
        <Label className="mt-3">Amount (AED)</Label>
        <Input type="number" min="1" step="1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="e.g. 20" autoFocus />
        <Label className="mt-3">Expiry date</Label>
        <DatePicker selected={form.exp ? new Date(form.exp) : null} onChange={(d) => setForm((f) => ({ ...f, exp: d ? moment(d).endOf('day').valueOf() : null }))} dateFormat="dd/MM/yyyy" minDate={new Date()} className="form-control" />
        <Label className="mt-3">Minimum order value to redeem <span className="text-muted">(optional)</span></Label>
        <Input type="number" min="0" value={form.minAmount} onChange={(e) => setForm((f) => ({ ...f, minAmount: e.target.value }))} />
        <div className="text-small text-muted mt-3">The customer gets a push ("{form.description === 'Voucher' ? 'Congratulations! You Have a New Voucher' : 'Compensation Granted'}") and the credit appears in their wallet immediately.</div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={dismiss} disabled={busy}>Cancel</Button>
        <Button color="primary" disabled={!valid || busy} onClick={submit}>{busy ? 'Issuing…' : `Issue ${form.description === 'Voucher' ? 'voucher' : 'compensation'}`}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default CompensateModal;
